import "./global.css";

import React, { useEffect, Suspense, lazy } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { createRoot } from "react-dom/client";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import Success from "./pages/Success";
import Cancel from "./pages/Cancel";
import Header from "./components/Header";
import Footer from "./components/Footer";
import PartnersBar from "./components/PartnersBar";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LocaleProvider } from "./context/LocaleContext";
import { useLocation } from "react-router-dom";

// Lazy load non-critical pages
const Index = lazy(() => import("./pages/Index"));
const ValuePage = lazy(() => import("./pages/Value"));
const TemplatePage = lazy(() => import("./pages/Template"));
const AdminPage = lazy(() => import("./pages/Admin"));
const AdminLoginPage = lazy(() => import("./pages/AdminLogin"));
const AboutPage = lazy(() => import("./pages/About"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const ContactPage = lazy(() => import("./pages/Contact"));
const BlogPage = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));
const AuditFormPage = lazy(() => import("./pages/AuditForm"));
const CaseStudiesPage = lazy(() => import("./pages/CaseStudies"));
const CaseStudyPage = lazy(() => import("./pages/CaseStudy"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-white text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p>Loading...</p>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const queryClient = new QueryClient();

function App() {
  useEffect(() => {
    // Service worker disabilitato per evitare richieste extra e complessità di caching
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      try {
        navigator.serviceWorker.getRegistrations?.().then((regs) => {
          regs.forEach((r) => r.unregister());
        });
      } catch (err) {
        console.warn("Service worker unregister failed", err);
      }
    }
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <LocaleProvider>
              <AuthProvider>
                <ErrorBoundary>
                  <CartProvider>
                    <ScrollToTop />
                    <Header />
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/en" element={<Home />} />
                      <Route
                        path="/products"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <Index />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/products"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <Index />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/template/:id"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <TemplatePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/template/:id"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <TemplatePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/chi-siamo"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AboutPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/about"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AboutPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/valore"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <ValuePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/value"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <ValuePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/pricing"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PricingPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/pricing"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PricingPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/contattaci"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <ContactPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/contact"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <ContactPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AdminPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/admin"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AdminPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/admin-login"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AdminLoginPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/admin-login"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AdminLoginPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/blog"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <BlogPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/blog"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <BlogPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/blog/:slug"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <BlogPostPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/blog/:slug"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <BlogPostPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/audit"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AuditFormPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/audit"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AuditFormPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/case-studies"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <CaseStudiesPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/case-studies"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <CaseStudiesPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/case-study/:slug"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <CaseStudyPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/en/case-study/:slug"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <CaseStudyPage />
                          </Suspense>
                        }
                      />
                      <Route path="/success" element={<Success />} />
                      <Route path="/en/success" element={<Success />} />
                      <Route path="/cancel" element={<Cancel />} />
                      <Route path="/en/cancel" element={<Cancel />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <PartnersBar />
                    <Footer />
                    <FloatingWhatsApp />
                  </CartProvider>
                </ErrorBoundary>
              </AuthProvider>
            </LocaleProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

// Create / reuse a global root to avoid calling createRoot multiple times during HMR
const container = document.getElementById("root");
const anyWindow = window as unknown as any;
// Install global handlers once to ignore noisy iframe evaluation timeouts from third-party iframes
if (!anyWindow.__fusion_listeners) {
  anyWindow.__fusion_listeners = true;
  if (typeof window !== "undefined") {
    window.addEventListener("error", (e: ErrorEvent) => {
      const msg = (e && e.error && e.error.message) || e.message || "";
      if (
        typeof msg === "string" &&
        msg.includes("IFrame evaluation timeout")
      ) {
        e.preventDefault();
      }
    });
    window.addEventListener(
      "unhandledrejection",
      (ev: PromiseRejectionEvent) => {
        const r = (ev && ev.reason && (ev.reason.message || ev.reason)) || "";
        const msg = typeof r === "string" ? r : r?.message;
        if (
          typeof msg === "string" &&
          msg.includes("IFrame evaluation timeout")
        ) {
          ev.preventDefault();
        }
      },
    );
  }
}
if (!anyWindow.__app_root) {
  anyWindow.__app_root = createRoot(container);
}
anyWindow.__app_root.render(<App />);
